/* istanbul ignore file */
import React, { memo } from "react";
import { isNull } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";
import {
  showNotifier,
  setAdjQtyError,
  setPrevAdjQty,
} from "../../store/globalSlice";
import useStableCallback from "../../hooks/useStableCallback";
import { isAdjQtyValid } from "../../utils";

const AutoStepTime = 150;
const AutoInputUpperLimit = 9999;
const AutoProps = (adjQtyError) => ({
  min: 0,
  max: 9999,
  style: { backgroundColor: adjQtyError ? "rgba(255, 0, 0, 0.7)" : "inherit" },
});
const AutoInputProps = { style: { height: "100%" } };

let timer, correctValueTimer;

const AutoStepInput = (props) => {
  const { id, value, field, getProcessedRow, stepper } = props;
  const dispatch = useDispatch();
  const apiRef = useGridApiContext();
  const prevAdjQty = useSelector((store) => store.global.prevAdjQty);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);

  const handleKeyDown = useStableCallback((e) => {
    if (["-", "."].includes(e.key)) {
      e.preventDefault();
    }
  });

  const updateValue = useStableCallback((val) => {
    apiRef.current.setEditCellValue({ id, field, value: val });
    getProcessedRow({ id, [field]: val }, field);
  });

  const handleBlur = useStableCallback((event) => {
    const value = event.target.value;
    if (value === "") {
      updateValue(0);
    } else {
      updateValue(value);
    }
  });

  const handleShowError = useStableCallback((number = 0) => {
    timer = setTimeout(() => {
      dispatch(
        showNotifier({
          severity: "error",
          message: isAdjQtyValid(number)
            ? "Invalid quantity value"
            : `Quantity value of ${number} is not allowed`,
        })
      );
      dispatch(setAdjQtyError(id));
    }, AutoStepTime);
  });

  const correctValue = useStableCallback((number) => {
    clearTimeout(correctValueTimer);
    correctValueTimer = setTimeout(() => {
      updateValue(number);
    }, AutoStepTime);
  });

  const handleValueChange = useStableCallback((event) => {
    let newValue = event?.target?.value;
    clearTimeout(timer);
    clearTimeout(correctValueTimer);
    if (!prevAdjQty && field === "adjqty") {
      dispatch(setPrevAdjQty(value));
    }
    if (adjQtyError) {
      dispatch(setAdjQtyError(null));
    }
    const currentValue = !isNull(newValue) ? Number(newValue) : null;
    newValue = currentValue;
    if (currentValue > AutoInputUpperLimit || isNaN(currentValue)) {
      return event.preventDefault();
    }
    updateValue(newValue);
    if (!isNaN(newValue)) {
      if (newValue % stepper === 0 && isAdjQtyValid(newValue)) {
        correctValue(newValue);
        dispatch(setAdjQtyError(null));
        dispatch(setPrevAdjQty(null));
      } else {
        handleShowError(newValue);
      }
    } else {
      handleShowError();
    }
    return true;
  });

  const autoInputProps = AutoProps(adjQtyError);

  return (
    <TextField
      fullWidth
      size="small"
      label=""
      name="adjQty"
      autoFocus
      value={value}
      onChange={handleValueChange}
      inputProps={autoInputProps}
      InputProps={AutoInputProps}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
};

export default memo(AutoStepInput);
